import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../../common/loader";
import Breadcrums from "../../common/breadcrumbs";
import { getTransactionDetails } from "../../services/paymentManagement.services";
import { formateDateWithMonth } from "../../../../utils/commonfunction";

const TransactionView = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id, source } = useParams();
    const [data, setData] = useState(null);
    const [loader, setLoader] = useState(true);

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: t("Transaction Details"), url: "" },
    ];

    useEffect(() => {
        if (id && source) {
            setLoader(true);
            getTransactionDetails({ id, source })
                .then((res) => {
                    if (res.success) {
                        setData(res.data);
                    }
                    setLoader(false);
                })
                .catch((err) => {
                    console.error(err);
                    setLoader(false);
                });
        }
    }, [id, source]);

    const getBackUrl = () => {
        switch (source) {
            case 'wallet': return "/admin/payment-management/wallet-history/1";
            case 'payment': return "/admin/payment-management/gateway-payments/1";
            case 'order': return "/admin/payment-management/order-payments/1";
            case 'consultation': return "/admin/payment-management/consultation-payments/1";
            default: return "/admin/payment-management/summary";
        }
    };

    if (loader) return <Loader />;
    if (!data) return <div className="text-center p-5">{t("msg_no_records")}</div>;

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="main-content-label mb-0">
                                {t("Transaction Details")} - {data.order_number || data.transaction_id || `#${id}`}
                            </h5>
                            <div>
                                {data.invoice_pdf && (
                                    <a
                                        href={data.invoice_pdf}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-primary me-2"
                                        title="View Invoice PDF"
                                    >
                                        <i className="fa fa-file-pdf-o me-1"></i> View Invoice PDF
                                    </a>
                                )}
                                <button onClick={() => navigate(getBackUrl())} className="btn btn-secondary">
                                    <i className="fa fa-arrow-left me-2"></i> {t("btn_back")}
                                </button>
                            </div>
                        </div>

                        <div className="row">
                            {/* Primary Details */}
                            <div className="col-lg-6 mb-4">
                                <div className="card border shadow-sm h-100">
                                    <div className="card-header bg-primary text-white py-2">
                                        <h6 className="mb-0">{t("Basic Information")}</h6>
                                    </div>
                                    <div className="card-body">
                                        <table className="table table-sm table-borderless mb-0">
                                            <tbody>
                                                {(data.order_number || source === 'wallet' || source === 'order') && (
                                                    <tr>
                                                        <td className="fw-bold ps-0" style={{ width: '40%' }}>{t("Order Number")}</td>
                                                        <td className="text-primary fw-bold">{data.order_number || (source === 'wallet' ? `WAL-${data.id}` : `#${id}`)}</td>
                                                    </tr>
                                                )}
                                                <tr>
                                                    <td className="fw-bold ps-0" style={{ width: '40%' }}>{t("Transaction ID")}</td>
                                                    <td className="text-dark">{data.transaction_id || (source === 'wallet' ? `TXN-WAL-${data.id}` : `#${id}`)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold ps-0">{source === 'order' ? t("Net Amount") : t("Amount")}</td>
                                                    <td className="fs-18">
                                                        <span className={`fw-bold ${source === 'wallet' && data.type === 'debit' ? 'text-danger' : 'text-success'}`}>
                                                            {source === 'wallet' && (data.type === 'credit' ? '+' : '-')} {data.currency === 'USD' || data.currency_type === 'USD' ? '$' : '₹'}{parseFloat(data.amount || (source === 'order' ? data.net_amount : data.total_amount) || 0).toFixed(2)}
                                                        </span>
                                                    </td>
                                                </tr>
                                                {source === 'order' && (
                                                    <>
                                                        <tr>
                                                            <td className="fw-bold ps-0">{t("Sub Total")}</td>
                                                            <td className="text-dark">₹{parseFloat(data.total_amount || 0).toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="fw-bold ps-0">{t("Delivery Charge")}</td>
                                                            <td className="text-dark">₹{parseFloat(data.shipping_amount || 0).toFixed(2)}</td>
                                                        </tr>
                                                    </>
                                                )}
                                                {source === 'wallet' && (data.gst_percent > 0 || data.gst_amount > 0) && (
                                                    <>
                                                        <tr>
                                                            <td className="fw-bold ps-0">{t("GST (%)")}</td>
                                                            <td className="text-dark">{parseFloat(data.gst_percent || 0).toFixed(2)}%</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="fw-bold ps-0">{t("GST Amount")}</td>
                                                            <td className="text-dark">{data.currency === 'USD' || data.currency_type === 'USD' ? '$' : '₹'}{parseFloat(data.gst_amount || 0).toFixed(2)}</td>
                                                        </tr>
                                                    </>
                                                )}
                                                <tr>
                                                    <td className="fw-bold ps-0">{t("Date & Time")}</td>
                                                    <td className="text-muted">{data.createdAt ? formateDateWithMonth(data.createdAt) : 'N/A'}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold ps-0">{t("Status")}</td>
                                                    <td>
                                                        <span className={`badge bg-${(data.status === 'success' || data.payment_status === 'success') ? 'success' : 'warning'} text-capitalize`}>
                                                            {data.status || data.payment_status || 'Pending'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* User details */}
                            <div className="col-lg-6 mb-4">
                                <div className="card border shadow-sm h-100">
                                    <div className="card-header bg-info text-white py-2">
                                        <h6 className="mb-0">{t("User Information")}</h6>
                                    </div>
                                    <div className="card-body">
                                        <table className="table table-sm table-borderless mb-0">
                                            <tbody>
                                                <tr>
                                                    <td className="fw-bold ps-0" style={{ width: '40%' }}>{source === 'wallet' ? t("User") : t("Name")}</td>
                                                    <td className="text-dark fw-bold">{source === 'wallet' ? (data.user?.name || 'N/A') : (data.user?.name || data.astrologer?.name || data.full_name || 'N/A')}</td>
                                                </tr>
                                                {source === 'wallet' && (() => {
                                                    const astroName = data.associated_astrologer?.name || data.astrologer?.name;
                                                    return astroName && astroName.toUpperCase() !== 'N/A';
                                                })() && (
                                                    <tr>
                                                        <td className="fw-bold ps-0">{t("Astrologer")}</td>
                                                        <td className="text-dark fw-bold">{data.associated_astrologer?.name || data.astrologer?.name}</td>
                                                    </tr>
                                                )}
                                                <tr>
                                                    <td className="fw-bold ps-0">{t("Email / Phone")}</td>
                                                    <td className="text-dark">{data.user?.email || data.user?.phone_number || data.mobile_number || 'N/A'}</td>
                                                </tr>
                                                {source === 'wallet' && (
                                                    <tr>
                                                        <td className="fw-bold ps-0">{t("User Type")}</td>
                                                        <td>
                                                            <span className={`badge text-capitalize bg-${data.user_type === 'admin' ? 'warning text-dark' : data.user_type === 'astrologer' ? 'info' : 'primary'}`}>
                                                                {data.user_type === 'admin' ? t("Admin") : data.user_type === 'astrologer' ? t("Astrologer") : t("User")}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                )}
                                                {(source === 'payment' || source === 'order') && (
                                                    <tr>
                                                        <td className="fw-bold ps-0">{t("Payment Method")}</td>
                                                        <td className="text-uppercase">{data.payment_method || 'N/A'}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TransactionView;
